/**
 * Filters out properties from the input object that have empty values.
 *
 * An empty value is defined as:
 * - `null`
 * - `undefined`
 * - An empty string (after trimming)
 * - `NaN`
 *
 * The function retains:
 * - Non-empty strings
 * - Numbers (including 0)
 * - Booleans (including false)
 * - Bigints
 * - Symbols
 *
 * @param obj - The input object with properties to be filtered.
 * @returns A new object with only the non-empty properties.
 *
 * @example
 * ```typescript
 * const params = {
 *     pageNumber: 1,
 *     pageSize: 10,
 *     search: '',
 *     sortDir: 'asc',
 *     sortBy: 'name'
 * };
 *
 * const filteredParams = filterNonEmptyProperties(params); // { pageNumber: 1, pageSize: 10, sortDir: 'asc', sortBy: 'name' }
 *
 * ```
 */
export const filterNonEmptyProperties = <T = Record<string, unknown>>(obj: T): Partial<T> => {
    return Object.entries(obj)
        .filter(([, value]) => {
            if (typeof value === "string") {
                return value.trim() !== "";
            }
            return value || value === false || value === 0;
        })
        .reduce<Partial<T>>((acc, [key, value]) => {
            acc[key] = value;
            return acc;
        }, {});
};

export const renameProperty = <T extends object, K extends keyof T, N extends string>(obj: T, oldProp: K, newProp: N): Omit<T, K> & Record<N, T[K]> => {
    const { [oldProp]: value, ...rest } = obj;
    return { ...rest, [newProp]: value } as Omit<T, K> & Record<N, T[K]>;
};

/**
 * Picks specific properties from an object.
 * @param obj - The input object.
 * @param keys - The keys of the properties to pick.
 * @returns A new object with only the picked properties.
 *
 * @example
 * ```typescript
 * const obj = { a: 1, b: 2, c: 3 };
 * const picked = pick(obj, ['a', 'c']); // { a: 1, c: 3 }
 * ```
 */
export const pick = <T extends object, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> => {
    return keys.reduce((acc, key) => ({ ...acc, [key]: obj[key] }), {} as Pick<T, K>);
};

/**
 * Compares two objects and returns an array of keys that are different.
 *
 * @param obj1 - The first object to compare.
 * @param obj2 - The second object to compare.
 * @returns An array of keys that are different between the two objects.
 *
 * @example
 * ```typescript
 * const obj1 = { a: 1, b: 2, c: 3 };
 * const obj2 = { a: 1, b: 2, c: 4 };
 * const diff = compare(obj1, obj2); // ["c"]
 * ```
 */
export const compare = <T extends object>(obj1: T, obj2: T): (keyof T)[] => {
    const keys = Object.keys(obj1) as (keyof T)[];
    return keys.filter((key) => obj1[key] !== obj2[key]);
};

/**
 * Checks if an object has any non-empty values.
 *
 * @param obj - The object to check.
 * @returns `true` if the object has any non-empty values, `false` otherwise.
 *
 * @example
 * ```typescript
 * const obj = { a: 1, b: 2, c: 3 };
 * const hasValues = hasAnyValues(obj); // true
 *
 * const obj2 = { a: null, b: undefined, c: "" };
 * const hasValue = hasAnyValues(obj2); // false
 * ```
 */
export const hasAnyValues = (obj: object): boolean => {
    return Object.values(obj).filter(Boolean).length > 0;
};
